import { useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { TextSizeToggle } from './TextSizeToggle';
import { ReturnBanner } from './ReturnBanner';
import { RAISE_IT_HEADER_URL } from './raise-it-links';

const linkBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 44,
  padding: '0 0.5rem',
  fontSize: '0.95rem',
  lineHeight: 1,
  textDecoration: 'none',
};

const activeProps = {
  style: { ...linkBase, color: 'var(--green)', fontWeight: 600 },
  'aria-current': 'page' as const,
};
const inactiveProps = { style: { ...linkBase, color: 'var(--ink-mid)' } };

export function SiteHeader() {
  const headerRef = useRef<HTMLElement | null>(null);

  // Publish the live header height as --header-h so anchored sections can
  // offset their scroll position past the sticky header.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-h', `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <header
      ref={headerRef}
      className="no-print site-header"
      style={{
        borderBottom: '1px solid var(--rule)',
        background: 'color-mix(in oklab, var(--cream) 92%, transparent)',
        backdropFilter: 'saturate(150%) blur(8px)',
        WebkitBackdropFilter: 'saturate(150%) blur(8px)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        paddingTop: 'env(safe-area-inset-top, 0)',
      }}
    >
      <ReturnBanner />
      <div
        className="site-header__inner"
        style={{
          width: 'min(100%, 1100px)',
          margin: '0 auto',
          padding: '0.5rem var(--sp-md)',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
        }}
      >
        <Link
          to="/"
          search={{}}
          aria-label="Warren Blackwood electorate guide — home"
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.2rem',
            color: 'var(--green)',
            letterSpacing: '0.01em',
            textDecoration: 'none',
          }}
        >
          Warren Blackwood electorate guide
        </Link>
        <nav
          aria-label="Primary"
          style={{
            display: 'flex',
            gap: '0.05rem',
            alignItems: 'center',
          }}
        >
          <Link
            to="/"
            search={{}}
            activeOptions={{ exact: true }}
            activeProps={activeProps}
            inactiveProps={inactiveProps}
            className="site-header__nav-link--desktop"
          >
            Map
          </Link>
          <Link
            to="/profile"
            activeProps={activeProps}
            inactiveProps={inactiveProps}
            className="site-header__nav-link--desktop"
          >
            Profile
          </Link>
          <Link
            to="/about"
            activeProps={activeProps}
            inactiveProps={inactiveProps}
            className="site-header__nav-link--desktop"
          >
            About
          </Link>
          <a
            href={RAISE_IT_HEADER_URL}
            className="cta-raise-it cta-raise-it--header"
            data-cta="raise-it-header"
          >
            Raise it with Bevan
          </a>
          <span style={{ marginLeft: '0.5rem' }}>
            <TextSizeToggle />
          </span>
        </nav>
      </div>
    </header>
  );
}
